/* eslint-disable react/require-default-props */
import React, {useEffect, useState} from 'react'
import {ThemeProvider} from './initThemes'
import PropTypes from 'prop-types'
import t from 'format-message'
import {Alert} from '@instructure/ui-alerts'
import {createRaven} from './raven'
import {initTranslations} from './initTranslations'
import {setUpDevHelpers} from './util/devHelpers'
import {initializeStore} from './launchParams'
import {store} from './store'
import {Provider} from 'react-redux'
import {createRouter} from './router'
import {RouterProvider} from 'react-router'

const raven = createRaven()

function captureInitializationError(error) {
  if (error) {
    raven.captureException(error)
  } else {
    raven.captureMessage('Failed to launch')
  }
}

export function App(props) {
  const [initializationStatus, setInitializationStatus] = useState('idle')
  const [router, setRouter] = useState(null)

  useEffect(() => {
    if (initializationStatus !== 'idle') {
      return
    }

    async function initApp() {
      setInitializationStatus('pending')

      const translations = await initTranslations()

      // If we do not have an access token, the session has expired & there isn't
      // much we can do about it.
      //
      // Send the user back to the last known return to url.
      if (!props.accessToken && props.returnToUrl) {
        window.location.replace(props.returnToUrl)
        return
      }

      // If we don't have an access token or a return url, something has gone
      // horribly wrong with the launch.
      //
      // Log an error and display an alert.
      if (!props.accessToken && !props.returnToUrl) {
        setInitializationStatus('error')
        captureInitializationError()
        return
      }

      setUpDevHelpers()
      initializeStore(store, {
        accessToken: props.accessToken,
        assignmentId: props.assignmentId,
        courseWorkflowState: props.courseWorkflowState,
        currentUser: props.currentUser,
        highContrast: props.highContrast,
        itemBanksScope: props.itemBanksScope,
        launchUrl: props.launchUrl,
        platform: props.platform,
        returnToUrl: props.returnToUrl,
        rldbLaunch: props.rldbLaunch,
        rcsHost: props.rcsHost,
        rcsJwt: props.rcsJwt,
        contextType: props.contextType,
        canvasContextId: props.canvasContextId,
        canvasLocalContextId: props.canvasLocalContextId,
        canvasToolId: props.canvasToolId,
        userCanvasId: props.userCanvasId,
        canvasHost: props.canvasHost,
        sdkTranslations: translations.quizSdk,
        moduleLaunch: props.moduleLaunch,
        canvasAssignmentId: props.canvasAssignmentId,
        timeZone: props.timeZone,
        restrictQuantitativeData: props.restrictQuantitativeData,
        gradingScheme: props.gradingScheme,
        launchToken: props.launchToken,
        readOnly: props.readOnly,
        decimalSeparator: props.decimalSeparator,
        thousandSeparator: props.thousandSeparator,
        aiQuizGeneration: props.aiQuizGeneration,
      })
    }

    initApp()
      .then(() => {
        setInitializationStatus('success')
      })
      .catch(error => {
        setInitializationStatus('error')
        captureInitializationError(error)
      })
  }, [initializationStatus, props])

  useEffect(() => {
    if (initializationStatus !== 'success' || router != null) {
      return
    }

    setRouter(createRouter())
  }, [initializationStatus, router])

  if (initializationStatus === 'pending') {
    return null
  }

  if (initializationStatus === 'error') {
    return (
      <Alert margin="large" variant="error">
        {t('Oops, something went wrong. We have logged the error and will investigate.')}
      </Alert>
    )
  }

  if (router == null) {
    return null
  }

  return (
    <ThemeProvider rldbLaunch={props.rldbLaunch} highContrast={props.highContrast}>
      <Provider store={store}>
        <RouterProvider router={router} />
      </Provider>
    </ThemeProvider>
  )
}

// eslint-disable-next-line immutable/no-mutation
App.propTypes = {
  accessToken: PropTypes.string,
  assignmentId: PropTypes.string,
  courseWorkflowState: PropTypes.string,
  currentUser: PropTypes.shape({}),
  highContrast: PropTypes.bool,
  itemBanksScope: PropTypes.shape({}),
  launchUrl: PropTypes.string,
  platform: PropTypes.string,
  returnToUrl: PropTypes.string,
  rldbLaunch: PropTypes.string,
  rcsHost: PropTypes.string,
  rcsJwt: PropTypes.string,
  contextType: PropTypes.string,
  canvasContextId: PropTypes.string,
  canvasLocalContextId: PropTypes.number,
  canvasToolId: PropTypes.string,
  userCanvasId: PropTypes.string,
  canvasHost: PropTypes.string,
  moduleLaunch: PropTypes.string,
  canvasAssignmentId: PropTypes.string,
  timeZone: PropTypes.string,
  restrictQuantitativeData: PropTypes.bool,
  gradingScheme: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.number,
    }),
  ),
  launchToken: PropTypes.string,
  readOnly: PropTypes.bool,
  decimalSeparator: PropTypes.string,
  thousandSeparator: PropTypes.string,
  aiQuizGeneration: PropTypes.bool,
}
